import {
  hash2,
  ROCK_HEIGHTS,
  spotsIn,
  Tile,
  tileAt,
  toScreen,
  type GameMap,
  type IsoOrigin,
  type TrainingSpotDef,
} from '@nine/shared';
import { canvas, ctx2d, ell, outline } from './pixel';
import type { Prop, ViewMapping } from './terrain';

/**
 * The training spots (data/trainingSpots.ts) in the world: what the map shaping leaves bare is
 * dressed here — the water down a waterfall's cliff, a cave's mouth, a ruined shrine, bamboo — and
 * each place has a little ambience of its own (spray, motes, steam, falling leaves, sparkle) over a
 * few rising motes of qi that every spot shares, so it reads as somewhere special. Sprites are drawn
 * once and cached; the ambience is a handful of pixels a frame, placed by hash and time.
 */

/** Frames of the falling water, swapped on the waterfall props as time passes. */
const FALL_FRAMES = 4;
const FALL_FPS = 9;

interface Placed {
  readonly spot: TrainingSpotDef;
  /** World pixels of the spot's centre on the ground. */
  readonly cx: number;
  readonly cy: number;
  /** Radius across the screen, in world pixels (the ground is squashed to half that down it). */
  readonly rx: number;
  /** Where the kind's own ambience comes from: the splash, the cave mouth, the burner, the knoll top. */
  readonly src: readonly [number, number] | null;
  /** Points the ambience spreads over: water tiles of a pool or the shore, bamboo tops. */
  readonly area: readonly (readonly [number, number])[];
}

const C = {
  waterDeep: '#2c74ad',
  water: '#4fa6dc',
  waterLit: '#93d6f1',
  foam: '#eefaff',
  ink: '#1b2433',
  stone: '#a49a88',
  stoneLit: '#c9bfab',
  stoneDark: '#756c5e',
  lacquer: '#a63a2c',
  lacquerDark: '#74261e',
  roof: '#3d6f6a',
  roofLit: '#5f9a8c',
  roofDark: '#284a4a',
  bamboo: '#5aa24a',
  bambooLit: '#9bd46c',
  bambooDark: '#3c7436',
  leaf: '#4d9440',
  leafLit: '#8fcc5e',
  caveIn: '#120d16',
  caveRim: '#3b3138',
  moss: '#5d8c3e',
} as const;

const cache = new Map<string, HTMLCanvasElement | HTMLCanvasElement[]>();
function cached<T extends HTMLCanvasElement | HTMLCanvasElement[]>(key: string, make: () => T): T {
  let v = cache.get(key) as T | undefined;
  if (!v) cache.set(key, (v = make()));
  return v;
}

/**
 * Water down the inside corner of the cliff, `h` world pixels from its lip to the ground, ending in
 * a splash: flat bands lit from the right, streaks running down a few pixels a frame.
 */
function fallFrames(h: number): HTMLCanvasElement[] {
  return cached(`fall${h}`, () => {
    const W = 18;
    const H = h + 9;
    const out: HTMLCanvasElement[] = [];
    for (let f = 0; f < FALL_FRAMES; f++) {
      const c = canvas(W, H);
      const g = ctx2d(c);
      for (let y = 0; y < H - 5; y++) {
        const hw = 3 + (y / H) * 2.5;
        const x0 = Math.round(W / 2 - hw);
        const x1 = Math.round(W / 2 + hw);
        for (let x = x0; x < x1; x++) {
          const u = (x - x0) / (x1 - x0);
          const streak = (y - f * 3 + Math.floor(hash2(x, 7) * 9)) % 9;
          g.fillStyle =
            y < 2
              ? C.foam
              : streak === 0 && u > 0.2
                ? C.foam
                : streak === 1 || u > 0.68
                  ? C.waterLit
                  : u < 0.28
                    ? C.waterDeep
                    : C.water;
          g.fillRect(x, y, 1, 1);
        }
      }
      // The splash at the foot: a ring of foam churning a pixel or two from frame to frame.
      ell(g, W / 2, H - 4, 8, 3.2, C.waterLit);
      ell(g, W / 2, H - 4.5, 6, 2.2, C.foam);
      g.fillStyle = C.foam;
      for (let k = 0; k < 6; k++) {
        const a = (k / 6) * Math.PI * 2 + f * 0.8;
        g.fillRect(
          Math.round(W / 2 + Math.cos(a) * 7),
          Math.round(H - 5 + Math.sin(a) * 2.5) - (k & 1),
          1,
          1,
        );
      }
      out.push(outline(c, C.ink));
    }
    return out;
  });
}

/** A cave cut into the corner of the rock: a dark arch, a rim of worn stone, a beard of moss. */
function caveMouth(): HTMLCanvasElement {
  return cached('cave', () => {
    const W = 26;
    const H = 30;
    const c = canvas(W, H);
    const g = ctx2d(c);
    const arch = (rx: number, top: number, col: string) => {
      g.fillStyle = col;
      for (let y = top; y < H - 1; y++) {
        const k = y - top < rx ? Math.sqrt(1 - ((rx - (y - top)) / rx) ** 2) : 1;
        const hw = Math.round(rx * k);
        g.fillRect(W / 2 - hw, y, hw * 2, 1);
      }
    };
    arch(12, 2, C.stoneDark);
    arch(11, 3, C.stone);
    // Lit along the right of the rim (the light is up and to the right).
    g.fillStyle = C.stoneLit;
    for (let y = 6; y < H - 2; y++) g.fillRect(W / 2 + 9 - (y < 9 ? 9 - y : 0), y, 2, 1);
    arch(8, 6, C.caveRim);
    arch(7, 8, C.caveIn);
    // A stone step at the threshold, and moss hanging from the lintel.
    g.fillStyle = C.stoneDark;
    g.fillRect(W / 2 - 8, H - 3, 16, 2);
    g.fillStyle = C.stoneLit;
    g.fillRect(W / 2 - 6, H - 3, 12, 1);
    g.fillStyle = C.moss;
    for (const x of [-6, -3, 1, 4, 7]) g.fillRect(W / 2 + x, 4 + (x & 1), 1, 2 + ((x + 9) % 3));
    return outline(c, C.ink);
  });
}

/** A small roadside shrine fallen into ruin: a stone plinth, faded red posts, a broken tiled roof. */
function ruinedShrine(): HTMLCanvasElement {
  return cached('shrine', () => {
    const W = 34;
    const H = 38;
    const c = canvas(W, H);
    const g = ctx2d(c);
    const r = (x: number, y: number, w: number, h: number, col: string) => {
      g.fillStyle = col;
      g.fillRect(x, y, w, h);
    };
    // Plinth in two steps.
    r(3, 31, 28, 5, C.stoneDark);
    r(3, 31, 28, 2, C.stone);
    r(20, 31, 11, 2, C.stoneLit);
    r(6, 27, 22, 4, C.stoneDark);
    r(6, 27, 22, 1, C.stone);
    r(18, 27, 10, 1, C.stoneLit);
    // Back wall in shadow, the posts in faded lacquer.
    r(9, 14, 16, 13, '#4a3a34');
    r(8, 13, 3, 14, C.lacquerDark);
    r(23, 13, 3, 14, C.lacquer);
    r(24, 13, 1, 14, '#c4594a');
    // A plaque, its writing long worn away.
    r(13, 16, 8, 5, '#6b5232');
    r(14, 17, 6, 3, '#8a6c42');
    // The roof: glazed tiles with upturned eaves, the left corner fallen in.
    r(5, 9, 25, 4, C.roofDark);
    r(7, 6, 21, 4, C.roof);
    r(17, 6, 11, 3, C.roofLit);
    r(10, 3, 15, 3, C.roof);
    r(18, 3, 7, 2, C.roofLit);
    r(12, 2, 11, 1, C.roofDark);
    r(29, 7, 3, 2, C.roof);
    r(31, 6, 1, 1, C.roofLit);
    g.clearRect(5, 9, 4, 2);
    g.clearRect(7, 6, 3, 2);
    for (const x of [8, 12, 16, 20, 24]) r(x, 10, 1, 3, '#1f3a3a');
    // An incense burner on the step.
    r(15, 23, 5, 4, '#5f5650');
    r(14, 22, 7, 1, '#7d736a');
    r(16, 21, 1, 1, '#d9a24a');
    r(18, 20, 1, 2, '#d9a24a');
    return outline(c, C.ink);
  });
}

/** A clump of bamboo: a few culms with knotted joints, a crown of leaves lit from the right. */
function bambooClump(v: number): HTMLCanvasElement {
  return cached(`bamboo${v}`, () => {
    const W = 22;
    const H = 58;
    const c = canvas(W, H);
    const g = ctx2d(c);
    const culms = [
      [5, 46],
      [9, 54],
      [13, 50],
      [17, 42],
    ].map(([x, h]) => [x! + ((v * 3 + x!) % 3) - 1, h! - ((v * 5 + x!) % 7)] as const);
    for (const [x, h] of culms) {
      for (let y = H - h; y < H - 1; y++) {
        g.fillStyle = (y - v * 2) % 9 === 0 ? C.bambooDark : C.bamboo;
        g.fillRect(x, y, 2, 1);
        g.fillStyle = (y - v * 2) % 9 === 0 ? C.bamboo : C.bambooLit;
        g.fillRect(x + 1, y, 1, 1);
      }
      // Leaves in sprays off the upper joints.
      for (let k = 0; k < 4; k++) {
        const y = H - h + 2 + k * 6;
        const side = (k + v + x) & 1 ? 1 : -1;
        g.fillStyle = k & 1 ? C.leafLit : C.leaf;
        for (let s = 1; s <= 4; s++) g.fillRect(x + side * (s + 1), y + (s >> 1), 1, 1);
        g.fillStyle = C.leaf;
        g.fillRect(x + side * 2, y + 2, 2, 1);
      }
    }
    return outline(c, '#1e2a1a');
  });
}

/** A low stone for the rim of a pool or the shore, a few pixels high. */
function lowStone(v: number, wide = false): HTMLCanvasElement {
  return cached(`stone${v}${wide ? 'w' : ''}`, () => {
    const W = wide ? 22 : 9;
    const H = wide ? 11 : 6;
    const c = canvas(W, H);
    const g = ctx2d(c);
    ell(g, W / 2, H / 2 + 0.5, W / 2 - 1, H / 2 - 1, C.stoneDark);
    ell(g, W / 2 + 0.5, H / 2 - 0.5, W / 2 - 2, H / 2 - 1.5, C.stone);
    ell(g, W / 2 + W / 6, H / 2 - 1, W / 5, 1, C.stoneLit);
    if (v & 1) {
      g.fillStyle = C.moss;
      g.fillRect(2, H - 3, 2, 1);
    }
    return outline(c, C.ink);
  });
}

/** The ambience's colours, a few fixed alphas so the pixels stay flat. */
const FX = {
  qi: ['#8ff0c0', '#e4fff0'],
  rim: ['rgba(120,240,190,.9)', 'rgba(230,255,240,.95)'],
  spray: ['rgba(238,250,255,.8)', 'rgba(190,232,248,.55)'],
  mist: 'rgba(226,242,250,.32)',
  mote: ['rgba(255,214,128,.85)', 'rgba(255,240,200,.95)'],
  steam: ['rgba(236,240,244,.42)', 'rgba(236,240,244,.24)'],
  leaf: ['#8fcc5e', '#c9df74'],
  spark: ['rgba(255,255,255,.95)', 'rgba(255,236,170,.8)'],
  ember: ['rgba(255,150,110,.85)', 'rgba(255,214,150,.9)'],
  smoke: 'rgba(214,210,220,.4)',
  wind: 'rgba(255,255,255,.35)',
} as const;

const frac = (x: number) => x - Math.floor(x);

export class SpotScenery {
  readonly props: Prop[] = [];
  private readonly falls: { prop: Prop; frames: HTMLCanvasElement[] }[] = [];
  private readonly placed: Placed[] = [];

  constructor(m: GameMap, o: IsoOrigin, view: ViewMapping) {
    const S = Math.SQRT1_2;
    /** A map tile (unturned) as seen in the turned view. */
    const tile = (i: number, j: number) => view.turn(i + 0.5, j + 0.5).map(Math.floor) as [number, number];
    const scr = (x: number, y: number) => toScreen(...view.turn(x, y), o);
    for (const s of spotsIn(m.zone)) {
      const { x, y, r } = s.area;
      const [cx, cy] = scr(x, y);
      let src: [number, number] | null = null;
      const area: [number, number][] = [];
      // Tiles of the spot (and of its backdrop) in the view, with their kind.
      const near: { i: number; j: number; t: number; d: number }[] = [];
      for (let j = Math.floor(y - r - 3); j <= Math.floor(y + r + 3); j++)
        for (let i = Math.floor(x - r - 3); i <= Math.floor(x + r + 3); i++) {
          const [ti, tj] = tile(i, j);
          near.push({ i: ti, j: tj, t: tileAt(m, ti, tj), d: Math.hypot(i + 0.5 - x, j + 0.5 - y) });
        }
      // The inside corner of a waterfall's or a cave's cliff: the tile in front of it (Q) and the
      // rock behind (T), on the diagonal toward the back of the view.
      const [qi, qj] = tile(Math.floor(x - (r - 0.2) * S), Math.floor(y - (r - 0.2) * S));
      const [bi, bj] = [qi - 1, qj - 1];
      const corner = toScreen(qi, qj, o);
      const rockBehind = tileAt(m, bi, bj) === Tile.Rock && m.variant[bj * m.w + bi]! >= 6;
      if (s.kind === 'waterfall' && rockBehind) {
        const h = ROCK_HEIGHTS[m.variant[bj * m.w + bi]!]!;
        const frames = fallFrames(h);
        const prop = { img: frames[0]!, x: corner[0] - 9, y: corner[1] - h - 2, d: qi + qj + 0.2 };
        this.props.push(prop);
        this.falls.push({ prop, frames });
        src = [corner[0], corner[1] + 3];
      } else if (s.kind === 'cave' && rockBehind) {
        const img = caveMouth();
        this.props.push({
          img,
          x: corner[0] - img.width / 2,
          y: corner[1] - img.height + 2,
          d: qi + qj + 0.2,
        });
        src = [corner[0], corner[1] - 10];
      } else if (s.kind === 'shrine') {
        // Over the solid ground the map left for it: the middle of those tiles.
        const ground = near.filter((n) => n.t === Tile.Building && n.d >= r && n.d < r + 1.5);
        if (ground.length) {
          const ax = ground.reduce((a, n) => a + n.i + 0.5, 0) / ground.length;
          const ay = ground.reduce((a, n) => a + n.j + 0.5, 0) / ground.length;
          const [sx, sy] = toScreen(ax, ay, o);
          const img = ruinedShrine();
          this.props.push({
            img,
            x: Math.round(sx - img.width / 2),
            y: Math.round(sy - img.height + 10),
            d: ax + ay + 0.5,
          });
          // The burner on the step (see `ruinedShrine`).
          src = [Math.round(sx) + 1, Math.round(sy) - 9];
        }
      } else if (s.kind === 'bamboo') {
        for (const n of near) {
          if (n.t !== Tile.Building || n.d < r || n.d > r + 2.5) continue;
          const img = bambooClump(Math.floor(hash2(n.i * 5 + 1, n.j * 7 + 3) * 3));
          const [sx, sy] = toScreen(n.i + 0.5, n.j + 0.5, o);
          this.props.push({
            img,
            x: Math.round(sx - img.width / 2),
            y: Math.round(sy - img.height + 4),
            d: n.i + n.j + 1,
          });
          area.push([sx, sy - img.height + 8]);
        }
      } else if (s.kind === 'hotSpring' || s.kind === 'shore') {
        // Stones round the water's edge; the water itself breathes steam or throws up spray.
        for (const n of near) {
          if (n.d >= r + (s.kind === 'shore' ? 2 : 0.5)) continue;
          const [sx, sy] = toScreen(n.i + 0.5, n.j + 0.5, o);
          if (n.t === Tile.Water) area.push([sx, sy]);
          const wet = [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
          ].some(([a, b]) => tileAt(m, n.i + a!, n.j + b!) === Tile.Water);
          const h = hash2(n.i * 13 + 5, n.j * 11 + 7);
          // The shore's sitting rocks along every wet tile; a hot spring's rim, here and there.
          if (n.t === Tile.Floor && wet && n.d < r && (s.kind === 'shore' || h < 0.7)) {
            const img = lowStone(Math.floor(h * 10), s.kind === 'shore');
            this.props.push({
              img,
              x: Math.round(sx - img.width / 2 + (h - 0.35) * 10),
              y: Math.round(sy - img.height + 2),
              d: n.i + n.j + 0.6,
            });
          }
        }
      } else if (s.kind === 'peak') src = [cx, cy - 22];
      this.placed.push({ spot: s, cx, cy, rx: r * 16, src, area });
    }
  }

  /** Moves the falling water on (the props' images are swapped, like the tall grass bending). */
  animate(t: number): void {
    const f = Math.floor(t * FALL_FPS) % FALL_FRAMES;
    for (const w of this.falls) w.prop.img = w.frames[f]!;
  }

  /** The spots' ambience over the world, for the spots on screen. */
  draw(g: CanvasRenderingContext2D, ox: number, oy: number, vw: number, vh: number, t: number): void {
    for (const p of this.placed) {
      const x = p.cx + ox;
      const y = p.cy + oy;
      if (x < -p.rx - 60 || x > vw + p.rx + 60 || y < -100 || y > vh + 60) continue;
      this.qi(g, p, x, y, t);
      const s = p.src && ([p.src[0] + ox, p.src[1] + oy] as const);
      switch (p.spot.kind) {
        case 'waterfall':
          if (s) this.spray(g, s[0], s[1], t);
          break;
        case 'cave':
          if (s) this.motes(g, s[0], s[1], t, FX.mote);
          break;
        case 'shrine':
          if (s) this.smoke(g, s[0], s[1], t);
          if (s) this.motes(g, s[0], s[1] + 10, t * 0.6, FX.mote, 3);
          break;
        case 'peak':
          this.sparkle(g, x, y - 18, p.rx, t, p.spot.zone === 'cinnabar' ? FX.ember : FX.spark);
          break;
        case 'bamboo':
          this.leaves(g, p, ox, oy, t);
          break;
        case 'hotSpring':
          this.steam(g, p, ox, oy, t);
          break;
        case 'shore':
          this.surf(g, p, ox, oy, t);
          break;
      }
    }
  }

  /**
   * Every spot: motes of qi rising slowly off the ground, a pale core in a jade cross while fresh;
   * and round the edge of the circle, points of light blinking on and off, so its bounds can be seen.
   */
  private qi(g: CanvasRenderingContext2D, p: Placed, x: number, y: number, t: number): void {
    for (let k = 0; k < 10; k++) {
      const life = frac(t * 0.16 + hash2(k, 3));
      if (life > 0.9) continue;
      const a = hash2(k, 11) * Math.PI * 2;
      const d = Math.sqrt(hash2(k, 17)) * 0.9;
      const mx = Math.round(x + Math.cos(a) * d * p.rx + Math.sin(t * 1.3 + k) * 2);
      const my = Math.round(y + Math.sin(a) * d * p.rx * 0.5 - life * 34);
      if (life < 0.55) {
        g.fillStyle = FX.qi[0]!;
        g.fillRect(mx - 1, my, 3, 1);
        g.fillRect(mx, my - 1, 1, 3);
      }
      g.fillStyle = FX.qi[life < 0.55 ? 1 : 0]!;
      g.fillRect(mx, my, 1, 1);
    }
    const n = Math.max(12, Math.round(p.rx / 3));
    for (let k = 0; k < n; k++) {
      const s = Math.sin(t * 1.7 + hash2(k, 83) * 6.3);
      if (s < 0.55) continue;
      const a = (k / n) * Math.PI * 2;
      const rx = Math.round(x + Math.cos(a) * p.rx);
      const ry = Math.round(y + Math.sin(a) * p.rx * 0.5);
      g.fillStyle = FX.rim[s > 0.85 ? 1 : 0]!;
      g.fillRect(rx, ry - 1, 1, s > 0.85 ? 2 : 1);
    }
  }

  /** A waterfall's spray thrown up off the splash, and mist drifting from it. */
  private spray(g: CanvasRenderingContext2D, x: number, y: number, t: number): void {
    for (let k = 0; k < 12; k++) {
      const life = frac(t * 1.4 + hash2(k, 5));
      const vx = (hash2(k, 9) - 0.5) * 26;
      const up = 6 + hash2(k, 13) * 8;
      g.fillStyle = FX.spray[k & 1]!;
      g.fillRect(Math.round(x + vx * life), Math.round(y - up * life * (2 - life) * 1.3 + life * 4), 1, 1);
    }
    g.fillStyle = FX.mist;
    for (let k = 0; k < 5; k++) {
      const life = frac(t * 0.22 + hash2(k, 21));
      const mx = Math.round(x + (hash2(k, 23) - 0.5) * 30 + life * 14);
      const my = Math.round(y - 2 - life * 10);
      // A dithered puff: every other pixel, so it stays pixel art.
      for (let v = 0; v < 3; v++) for (let u = v & 1; u < 6; u += 2) g.fillRect(mx + u, my + v, 1, 1);
    }
  }

  /** Glowing motes drifting out of a cave mouth or round a shrine, blinking as they go. */
  private motes(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    t: number,
    cols: readonly string[],
    n = 6,
  ): void {
    for (let k = 0; k < n; k++) {
      const life = frac(t * 0.25 + hash2(k, 31));
      const dx = (hash2(k, 33) - 0.5) * 34 * life;
      const dy = (6 + hash2(k, 37) * 10) * life - life * life * 26;
      if (Math.sin(t * 5 + k * 2.1) < -0.4) continue;
      g.fillStyle = cols[k & 1]!;
      g.fillRect(Math.round(x + dx + Math.sin(t * 2 + k) * 2), Math.round(y + dy), life < 0.5 ? 2 : 1, 1);
    }
  }

  /** A thread of incense smoke rising from the burner, swaying as it thins. */
  private smoke(g: CanvasRenderingContext2D, x: number, y: number, t: number): void {
    g.fillStyle = FX.smoke;
    for (let k = 0; k < 9; k++) {
      const h = k * 2.5 + frac(t * 0.8) * 2.5;
      g.fillRect(
        Math.round(x + Math.sin(t * 1.2 - h * 0.18) * (h * 0.15)),
        Math.round(y - h),
        1,
        k < 6 ? 2 : 1,
      );
    }
  }

  /** Light catching the high rock: four-point twinkles, and now and then a gust across the top. */
  private sparkle(
    g: CanvasRenderingContext2D,
    x: number,
    y: number,
    rx: number,
    t: number,
    cols: readonly string[],
  ): void {
    for (let k = 0; k < 6; k++) {
      const s = Math.sin(t * (1.1 + hash2(k, 41)) + k * 1.7);
      if (s < 0.72) continue;
      const sx = Math.round(x + (hash2(k, 43) - 0.5) * rx * 1.6);
      const sy = Math.round(y + (hash2(k, 47) - 0.5) * rx * 0.7);
      g.fillStyle = cols[k & 1]!;
      g.fillRect(sx, sy, 1, 1);
      if (s > 0.9) {
        g.fillRect(sx - 1, sy, 3, 1);
        g.fillRect(sx, sy - 1, 1, 3);
      }
    }
    const gust = frac(t * 0.21);
    if (gust < 0.3) {
      g.fillStyle = FX.wind;
      const gx = Math.round(x - rx * 1.4 + (gust / 0.3) * rx * 2.8);
      g.fillRect(gx, Math.round(y - 10), 8, 1);
      g.fillRect(gx + 5, Math.round(y - 13), 6, 1);
    }
  }

  /** Bamboo leaves let go from the crowns and turning down to the ground. */
  private leaves(g: CanvasRenderingContext2D, p: Placed, ox: number, oy: number, t: number): void {
    if (!p.area.length) return;
    for (let k = 0; k < 8; k++) {
      const life = frac(t * 0.13 + hash2(k, 51));
      const [lx, ly] = p.area[Math.floor(hash2(k, 53) * p.area.length)]!;
      const fx = Math.round(lx + ox + Math.sin(t * 1.7 + k) * 5 + life * 10);
      const fy = Math.round(ly + oy + life * 48);
      g.fillStyle = FX.leaf[k & 1]!;
      g.fillRect(fx, fy, Math.sin(t * 3 + k) > 0 ? 2 : 1, 1);
    }
  }

  /** Steam off a hot spring: pale wisps rising from the water and thinning out. */
  private steam(g: CanvasRenderingContext2D, p: Placed, ox: number, oy: number, t: number): void {
    if (!p.area.length) return;
    for (let k = 0; k < 14; k++) {
      const life = frac(t * 0.3 + hash2(k, 61));
      const [wx, wy] = p.area[k % p.area.length]!;
      const sx = Math.round(wx + ox + (hash2(k, 63) - 0.5) * 22 + Math.sin(t * 1.4 + k) * 3);
      const sy = Math.round(wy + oy - life * 26);
      g.fillStyle = FX.steam[life < 0.5 ? 0 : 1]!;
      g.fillRect(sx, sy, 2, 1);
      if (life < 0.4) g.fillRect(sx + 1, sy - 1, 1, 1);
    }
  }

  /** Foam and glints along the water's edge by the rocks. */
  private surf(g: CanvasRenderingContext2D, p: Placed, ox: number, oy: number, t: number): void {
    for (let k = 0; k < p.area.length; k++) {
      const [wx, wy] = p.area[k]!;
      const s = Math.sin(t * 2.2 + hash2(k, 71) * 6.3);
      if (s < 0.5) continue;
      g.fillStyle = FX.spray[s > 0.85 ? 0 : 1]!;
      const sx = Math.round(wx + ox + (hash2(k, 73) - 0.5) * 16);
      const sy = Math.round(wy + oy + (hash2(k, 79) - 0.5) * 6);
      g.fillRect(sx, sy, s > 0.85 ? 3 : 2, 1);
    }
  }
}
